import { ErrorAlert } from "@components/ErrorAlert";
import {
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";

import {
  GetPayeeEngagementAsPayerExpandParameterInner,
  PayrollRunType,
  PayStatementStatus
} from "@wingspanhq/wingspan-api-sdk";
import {
  queriesPayeeEngagements,
  queriesPayeeRows,
  queriesWorkItems,
  queriesWorkLogs
} from "@query";
import { useSession } from "@hooks/useSession";
import {
  useModalWorkLogHistory,
  WorkLogHistoryWidget
} from "../../components/WorkLogHistoryWidget";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { buildPayeeDetailsPath } from "../../../Payees/utils";
import { useXWingspanPayerId } from "../../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { useMutationDownloadPaystubPdf } from "../../../PayrollRuns/hooks/useMutationDownloadPaystubPdf";
import { useModalRunOffCyclePayroll } from "../../../PayrollRuns/modals/ModalRunOffCyclePayroll";
import {
  routeHelperPayrollRunsDetails,
  routeHelperPayrollRunsPaystub
} from "../../../PayrollRuns/paths";
import { selectorPayrollRunsYear } from "../../../PayrollRuns/selectors/selectorPayrollRunsYear";
import { RateCardViewWidget } from "../../../RateCards/components/RateCardViewWidget";
import { AlertWorkLogReviewStatusPayer } from "../../components/AlertWorkLogReviewStatusPayer";
import { CardWorkItems } from "../../components/CardWorkItems";
import { activeWorkItemsFilter } from "../../utils/activeWorkItemsFilter";
import { PayrollDetails } from "../../components/PayrollDetails";
import { WorkLogAdditionalDetails } from "../../components/WorkLogAdditionalDetails";
import { WorkLogAssignmentInfoPayerView } from "../../components/WorkLogAssignmentInfoPayerView";
import { WorkLogStatusStepperPayerView } from "../../components/WorkLogStatusStepperPayerView";
import { WorkLogsInPayPeriod } from "../../components/WorkLogsInPayPeriod";
import { useBulkReviewWorkLogs } from "../../hooks/useBulkReviewWorkLogs";
import { useQueryWorkLogConvertedResource } from "../../hooks/useQueryWorkLogConvertedResource";
import { useQueryWorkLogPayrollRun } from "../../hooks/useQueryWorkLogPayrollRun";
import { useModalApproveWorkLog } from "../../modals/ModalApproveWorkLog";
import { useModalCancelWorkLogInvoice } from "../../modals/ModalCancelWorkLogInvoice";
import { useModalRejectWorkLog } from "../../modals/ModalRejectWorkLog";
import { useModalReopenWorkLog } from "../../modals/ModalReopenWorkLog";
import { useModalRequestChanges } from "../../modals/ModalRequestChanges";
import { routeHelpersWorkLogsPayer } from "../../paths";
import { selectorWorkLogApprovalDaysLeft } from "../../selectors/selectorWorkLogApprovalDaysLeft";
import { selectorWorkLogApprovalExpired } from "../../selectors/selectorWorkLogApprovalExpired";
import { selectorWorkLogConvertedResourceLabels } from "../../selectors/selectorWorkLogConvertedResourceLabels";
import { selectorWorkLogWorkerNoun } from "../../selectors/selectorWorkLogWorkerNoun";
import {
  canPayerApprove,
  canPayerEdit,
  canPayerReopen,
  hasPayeeRequestedEditing,
  isStatusDiscarded,
  isStatusInProgress
} from "../../utils/workLogPermissions";

export const RoutePayerWorkLogDetails: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const workLogId = props.match.params.workLogId;
  const { accountId } = useSession();
  const history = useHistory();
  const goToPaystub = routeHelperPayrollRunsPaystub.useGoTo();

  // Query work log
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery({ workLogId });
  const workLog = queryWorkLog.data;

  // An approved work log converts into a payable (1099 contractor) or a pay
  // statement (W-2 employee) — `convertedResourceType` says which, and the hook
  // gates each read on the matching type.
  const {
    convertedResourceId,
    isPayStatement: isConvertedToPayStatement,
    payStatement,
    queryPayable,
    queryPayStatement
  } = useQueryWorkLogConvertedResource(workLog);

  // Once the statement is paid the funds have moved: neither it nor the work log
  // behind it can be cancelled or reopened. A payable carries no equivalent
  // signal on v3, so this only guards the employee side.
  // TODO(v3): gate the contractor side too once `Payable` exposes a paid status.
  const isConvertedResourcePaid =
    payStatement?.status === PayStatementStatus.PAID;

  // A statement's PDF lives in the vault behind `fileId`; absent until the run
  // that generated it has produced one.
  const payStatementFileId = payStatement?.fileId ?? undefined;

  const xWingspanAccount = useXWingspanPayerId();
  const xWingspanExpansion = useXWingspanExpansion();

  // Query payee information
  const queryPayeeRow = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        engagements_id: {
          eq: workLog?.payeeEngagementId as string
        }
      },
      page: { size: 1 },
      xWingspanAccount: xWingspanExpansion
    },
    {
      enabled: !!workLog?.payeeEngagementId
    }
  );

  const payeeRow = queryPayeeRow.data?.data?.[0];
  const payeeId = payeeRow?.id;
  // TODO(v3): v1 `payerPayeeEngagementId` renamed to `payeeEngagementId`.
  const payerPayeeEngagementId = workLog?.payeeEngagementId;

  // v1 `GET /payee/engagement/{id}` (no payeeId) → v3 `getPayeeEngagementAsPayer`.
  const queryPayerPayeeEngagement =
    queriesPayeeEngagements.getPayeeEngagementAsPayer.useQuery(
      {
        payeeEngagementId: payerPayeeEngagementId!,
        expand: new Set([
          GetPayeeEngagementAsPayerExpandParameterInner.WORK_DEFINITIONS
        ]),
        xWingspanAccount
      },
      { enabled: !!payerPayeeEngagementId }
    );

  // Contractor name off the search row's server-resolved `profile.resolvedName`
  // — the same string the contractor list and work-log tables show.
  const contractorName = payeeRow ? selectorSearchRowName(payeeRow) : undefined;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  // Before approval `convertedResourceType` is null, so the engagement's own type
  // is what says whether this will become a pay statement or an invoice.
  const engagementType = payerPayeeEngagement?.engagementType;
  const workerNoun = selectorWorkLogWorkerNoun(engagementType);
  const convertedResourceLabels = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog?.convertedResourceType,
    engagementType
  });

  const workDefinitions = useMemo(
    () =>
      [...(queryPayerPayeeEngagement.data?.workDefinitions ?? [])].sort(
        (a, b) => (a.name ?? "").localeCompare(b.name ?? "")
      ),
    [queryPayerPayeeEngagement.data?.workDefinitions]
  );

  // Query work items
  const queryWorkItems = queriesWorkItems.listWorkItems.useQuery({
    filter: activeWorkItemsFilter(workLogId)
  });

  // Activate filtered review sidebar when ?bulkReview=true is in URL
  useBulkReviewWorkLogs(workLogId);

  // The payroll run this work log is tied to, and what that means for approving:
  // how long is left, or whether the window has already gone. `isLoading` gates
  // the expiry check so the page never flashes "expired" at a work log whose run
  // simply hasn't resolved yet.
  const { payrollRun, isLoading: isPayrollRunLoading } =
    useQueryWorkLogPayrollRun(workLog);

  const isApprovalExpired =
    !isPayrollRunLoading &&
    selectorWorkLogApprovalExpired({ workLog, payrollRun });

  const approvalDaysLeft = selectorWorkLogApprovalDaysLeft({ payrollRun });

  // Modals for payer actions
  const modalApprove = useModalApproveWorkLog();
  const modalRunOffCyclePayroll = useModalRunOffCyclePayroll();
  const modalReject = useModalRejectWorkLog();
  const modalRequestChanges = useModalRequestChanges();
  const modalReopen = useModalReopenWorkLog();
  const modalCancelInvoice = useModalCancelWorkLogInvoice();
  const modalWorkLogHistory = useModalWorkLogHistory();

  // Browser page title
  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId || !contractorName) {
      return "Work Log Details";
    }

    return payerPayeeEngagement
      ? `Work Log – ${contractorName} • ${payerPayeeEngagement.engagementName}`
      : `Work Log – ${contractorName}`;
  }, [contractorName, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  // Determine if View payable button should be disabled
  const isViewPayableDisabled = useMemo(() => {
    if (!convertedResourceId) {
      return true;
    }

    if (isConvertedToPayStatement) {
      // The paystub route is nested under its payroll run, so the statement has
      // to resolve before we can build the link.
      return !queryPayStatement.data?.payrollRunId;
    }

    const isDataEmptyObject =
      !!queryPayable.data && Object.keys(queryPayable.data).length === 0;

    const isPayableNotAccessible =
      (queryPayable.isError &&
        (queryPayable.error?.response?.status === 404 ||
          queryPayable.error?.response?.status === 403)) ||
      isDataEmptyObject;

    return isPayableNotAccessible;
  }, [
    convertedResourceId,
    isConvertedToPayStatement,
    queryPayStatement.data,
    queryPayable
  ]);

  // Action handlers
  const handleViewPayable = useCallback(() => {
    if (!convertedResourceId) {
      return;
    }

    if (isConvertedToPayStatement) {
      const payStatement = queryPayStatement.data;

      if (payStatement?.payrollRunId) {
        goToPaystub({
          year: selectorPayrollRunsYear(payStatement.payDate),
          payrollRunId: payStatement.payrollRunId,
          paystubId: convertedResourceId
        });
      }
      return;
    }

    history.push(`/member/invoices/payables/dashboard/${convertedResourceId}`);
  }, [
    convertedResourceId,
    isConvertedToPayStatement,
    queryPayStatement.data,
    goToPaystub,
    history
  ]);

  const handleApprove = useCallback(() => {
    if (workLog) {
      modalApprove.open({
        workLog,
        engagementType,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, engagementType, modalApprove, queryWorkLog]);

  const handleRequestChanges = useCallback(() => {
    if (workLog) {
      modalRequestChanges.open({
        workLog,
        contractorName,
        engagementType,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [
    workLog,
    contractorName,
    engagementType,
    modalRequestChanges,
    queryWorkLog
  ]);

  const handleReject = useCallback(() => {
    if (workLog) {
      modalReject.open({
        workLog,
        contractorName,
        engagementType,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, contractorName, engagementType, modalReject, queryWorkLog]);

  const handleReopenWorkLog = useCallback(() => {
    if (workLog) {
      modalReopen.open({
        workLog,
        contractorName,
        engagementType,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, contractorName, engagementType, modalReopen, queryWorkLog]);

  const handleCancelInvoice = useCallback(() => {
    if (workLog) {
      modalCancelInvoice.open({
        workLog,
        contractorName,
        engagementType,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [
    workLog,
    contractorName,
    engagementType,
    modalCancelInvoice,
    queryWorkLog
  ]);

  const handleViewHistory = useCallback(() => {
    modalWorkLogHistory.open({ workLogId });
  }, [modalWorkLogHistory, workLogId]);

  const [downloadPaystubPdf, downloadPaystubPdfMeta] =
    useMutationDownloadPaystubPdf();

  const handleDownloadPayStatementPdf = useCallback(() => {
    if (payStatementFileId) {
      downloadPaystubPdf({
        fileId: payStatementFileId,
        filename: `pay-statement-${payStatement?.payStatementNumber ?? ""}`
      });
    }
  }, [downloadPaystubPdf, payStatementFileId, payStatement]);

  const goToPayrollRun = routeHelperPayrollRunsDetails.useGoTo();

  // The only way to pay a work log whose approval window has closed. The modal
  // resolves with the run to open — the new one, or the period's already-open run
  // behind its "View payroll" link.
  const handleCreateOffCycleRun = useCallback(async () => {
    // Work logs are paid out by W-2 employee payroll; the modal renders outside
    // the PayrollRuns surface, so the type is passed explicitly.
    const payroll = await modalRunOffCyclePayroll.open({
      type: PayrollRunType.EMPLOYEE
    });

    if (payroll?.id) {
      goToPayrollRun({
        year: selectorPayrollRunsYear(payroll.payDate),
        payrollRunId: payroll.id
      });
    }
  }, [modalRunOffCyclePayroll, goToPayrollRun]);

  // Get status-aware toolbar actions for the payer
  const getToolbarActions = () => {
    const status = workLog?.status;

    // InReview: toolbar layout depends on whether the contractor has requested editing
    if (canPayerApprove(status)) {
      const editingRequested = hasPayeeRequestedEditing(workLog as any);

      if (editingRequested) {
        // Contractor requested editing: "Return for editing" (Secondary) + "Approve" (Primary) as buttons
        return [
          {
            label: "Return for editing",
            buttonKind: "Secondary" as const,
            onClick: handleRequestChanges,
            buttonOrder: 1
          },
          {
            label: "Approve",
            buttonKind: "Primary" as const,
            onClick: handleApprove,
            // Nothing left to approve into — the alert offers the off-cycle run.
            disabled: isApprovalExpired,
            buttonOrder: 2
          },
          {
            icon: "block" as const,
            label: "Reject",
            helpText: `Reject the work log so no ${convertedResourceLabels.noun} is created`,
            destructive: true,
            onClick: handleReject
          }
        ];
      }

      // Normal InReview: "Approve" (Primary) as button,
      //                   "Return for editing" + "Reject" in three-dot menu
      return [
        {
          label: "Approve",
          buttonKind: "Primary" as const,
          onClick: handleApprove,
          // Nothing left to approve into — the alert offers the off-cycle run.
          disabled: isApprovalExpired,
          buttonOrder: 1
        },
        {
          icon: "reset" as const,
          label: "Return for editing",
          helpText: `Send work log back to "In-progress" and ask the ${workerNoun} to make changes`,
          onClick: handleRequestChanges
        },
        {
          icon: "block" as const,
          label: "Reject",
          helpText: `Reject the work log so no ${convertedResourceLabels.noun} is created`,
          destructive: true,
          onClick: handleReject
        }
      ];
    }

    // Discarded: only "Reopen for changes" in the three-dot menu
    if (isStatusDiscarded(status as string)) {
      return [
        {
          icon: "reset" as const,
          label: "Reopen for changes",
          helpText: "Reopens the discarded work log for changes",
          onClick: handleReopenWorkLog
        }
      ];
    }

    // Approved: "View invoice" (Secondary) as toolbar button,
    //           menu items for Download PDF + Reopen + Cancel
    if (canPayerReopen(status)) {
      return [
        {
          label: convertedResourceLabels.view,
          buttonKind: "Secondary" as const,
          onClick: handleViewPayable,
          disabled: isViewPayableDisabled,
          buttonOrder: 1
        },
        ...(payStatementFileId
          ? [
              {
                icon: "download" as const,
                label: "Download PDF",
                loading: downloadPaystubPdfMeta.isLoading,
                onClick: handleDownloadPayStatementPdf
              }
            ]
          : []),
        {
          icon: "reset" as const,
          label: "Reopen for changes",
          helpText: `Cancels the unpaid ${convertedResourceLabels.noun} and reopens work log for changes`,
          // Once the money has moved neither the statement nor the work log can
          // change, so reopening has nothing to cancel and nothing to reopen.
          disabled: isConvertedResourcePaid,
          onClick: handleReopenWorkLog
        },
        ...(workLog?.convertedResourceId
          ? [
              {
                icon: "block" as const,
                label: convertedResourceLabels.cancel,
                helpText: isConvertedResourcePaid
                  ? `This ${convertedResourceLabels.noun} has been paid and can no longer be cancelled`
                  : `Cancels the ${convertedResourceLabels.noun} and the related work log`,
                destructive: true,
                disabled: isConvertedResourcePaid,
                onClick: handleCancelInvoice
              }
            ]
          : [])
      ];
    }

    return [];
  };

  // Error handling - 404 Not Found
  if (queryWorkLog.isError && queryWorkLog.error?.response?.status === 404) {
    return (
      <WSPage>
        <WSEmptyState
          actions={{
            buttons: [
              {
                label: "Back to work logs",
                onClick: () => history.push(routeHelpersWorkLogsPayer.path)
              }
            ]
          }}
          description="The work log you're looking for doesn't exist or has been deleted."
          title="Work log not found"
          type="search"
        />
      </WSPage>
    );
  }

  // Error handling - 403 Forbidden
  if (queryWorkLog.isError && queryWorkLog.error?.response?.status === 403) {
    return (
      <WSPage>
        <WSElement mt="2XL">
          <WSEmptyState
            actions={{
              buttons: [
                {
                  label: "Back to work logs",
                  onClick: () => history.push(routeHelpersWorkLogsPayer.path)
                }
              ]
            }}
            description="You don't have permission to view this work log."
            title="Access denied"
            type="error"
          />
        </WSElement>
      </WSPage>
    );
  }

  // Main content
  return (
    <WSPage>
      <WSPageToolbar
        actions={getToolbarActions()}
        breadcrumbs={[
          {
            label: "Work logs",
            onClick: () => history.push(routeHelpersWorkLogsPayer.path)
          },
          // Contractor > assignment > work log, per the design. Each crumb is
          // dropped until its name resolves, so the trail never shows a
          // placeholder; only the contractor has a detail screen to link to.
          ...(contractorName && payeeId
            ? [
                {
                  label: contractorName,
                  onClick: () => history.push(buildPayeeDetailsPath(payeeId))
                }
              ]
            : []),
          ...(payerPayeeEngagement?.engagementName
            ? [{ label: payerPayeeEngagement.engagementName }]
            : []),
          {
            label: workLog ? `Work log #${workLog.workLogNumber}` : "Work log"
          }
        ]}
        title={workLog ? `Work log #${workLog.workLogNumber}` : "Work log"}
      />

      <AlertWorkLogReviewStatusPayer
        workLog={workLog}
        contractorName={contractorName}
        approvalDaysLeft={approvalDaysLeft}
        engagementType={engagementType}
        isApprovalExpired={isApprovalExpired}
        isConvertedResourcePaid={isConvertedResourcePaid}
        onCreateOffCycleRun={handleCreateOffCycleRun}
        onRequestChanges={
          canPayerApprove(workLog?.status) ? handleRequestChanges : undefined
        }
        onViewConvertedResource={
          isViewPayableDisabled ? undefined : handleViewPayable
        }
        onViewHistory={handleViewHistory}
      />

      <WSGrid gutter="XL" mt="L">
        <WSGrid.Item span={{ m: "8" }}>
          <WSList gap="XL">
            <WorkLogAssignmentInfoPayerView
              isApprovalExpired={isApprovalExpired}
              payeeRow={payeeRow}
              payerPayeeEngagement={payerPayeeEngagement}
              shimmer={queryPayerPayeeEngagement.isInitialLoading}
              workLog={workLog}
            />

            <ErrorAlert
              role="alert"
              context="WorkLog"
              error={queryPayerPayeeEngagement.error}
            />
            <CardWorkItems
              contractorName={contractorName}
              currentUserRole="payer"
              noEdit={true}
              overlay={isStatusInProgress(workLog?.status)}
              otherPartyName={contractorName}
              payerEdit={canPayerEdit(workLog?.status)}
              payerId={accountId!}
              payerPayeeEngagementId={workLog?.payeeEngagementId!}
              resources={{ payerPayeeEngagement }}
              shimmer={
                queryWorkItems.isInitialLoading ||
                queryPayerPayeeEngagement.isInitialLoading
              }
              statusPill={
                isStatusInProgress(workLog?.status)
                  ? {
                      theme: "violet",
                      type: "light",
                      icon: "lock" as const,
                      text: "Returned for editing"
                    }
                  : undefined
              }
              workDefinitions={workDefinitions}
              workItems={queryWorkItems.data?.data || []}
              workLogId={workLogId}
              onViewHistory={handleViewHistory}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ m: "4" }}>
          <WSList gap="XL">
            <WorkLogStatusStepperPayerView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogsInPayPeriod
              currentUserRole="payer"
              payeeAccountId={payeeRow?.payeeAccountId}
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <PayrollDetails
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <RateCardViewWidget
              context="contractor-assignment"
              payeeEngagementId={payerPayeeEngagementId}
              payeeId={payeeId}
            />
            <WorkLogAdditionalDetails
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
              overlay={isStatusInProgress(workLog?.status)}
            />
            <WorkLogHistoryWidget workLogId={workLogId} />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
